import logoDark from '../../assets/liftx-lockup-dark.png';
import logoLight from '../../assets/liftx-lockup-light.png';
import { useTheme } from '../../app/ThemeProvider';

/**
 * The LiftX horizontal lockup (mark + wordmark + tagline).
 *
 * The ONLY sanctioned way to render this logo on unauthenticated
 * auth screens and Settings → About. Logo redesign pass:
 *   - transparent background, so it sits directly on whatever surface it's
 *     placed on rather than carrying a baked-in coloured panel;
 *   - height is constrained, width is never constrained — aspect ratio is
 *     preserved absolutely;
 *   - no recolouring, tinting, rotation, or effects (cropping was applied
 *     once, mechanically, to the supplied artwork's own huge empty canvas
 *     margins — every glyph pixel is untouched from the original).
 *
 * `liftx-lockup-dark.png` is the light-on-dark artwork (for dark
 * appearance); `liftx-lockup-light.png` is the dark-on-light artwork (for
 * light appearance).
 */
export function LiftXLogo({
  height = 32,
  className,
}: {
  height?: number;
  className?: string;
}): React.JSX.Element {
  const { resolved } = useTheme();

  return (
    <img
      src={resolved === 'dark' ? logoDark : logoLight}
      alt="LiftX"
      className={className}
      width={LOGO_INTRINSIC_WIDTH}
      height={LOGO_INTRINSIC_HEIGHT}
      style={{
        height,
        width: 'auto',
        display: 'block',
        /*
         * `alignSelf` and `flexShrink` are load-bearing, not decoration.
         *
         * A flex container defaults to `align-items: stretch`. As a direct
         * child of one, this image stretches along the cross axis and
         * `width: auto` loses — which silently distorts the wordmark. That is
         * exactly what Phase 0 §15.4 forbids, and it is invisible unless you
         * measure it, because the logo still "looks like a logo".
         */
        alignSelf: 'start',
        flexShrink: 0,
        /* If a narrow viewport ever clamps the width, letterbox rather than
           squash: the artwork's own proportions survive either way. */
        maxWidth: '100%',
        objectFit: 'contain',
      }}
      draggable={false}
    />
  );
}

/** Intrinsic size of both supplied assets, used to reserve layout space. */
const LOGO_INTRINSIC_WIDTH = 1089;
const LOGO_INTRINSIC_HEIGHT = 522;
